import i18n from "../../../language/i18n";
import { NotificationManager } from "../../../notifications/NotificationManager";

// Copy/paste guard for an OPEN_ENDED question being SOLVED.
//
// The loop this closes: lift the question out to an external AI, paste the answer back
// into the box. Both halves are blocked — `copy`/`cut` over the card (question stem,
// reading passage) and `paste`/`drop` in the answer textarea — so the learner has to
// write the answer themselves. It is a speed bump, not a security boundary: anyone can
// still retype the question or open devtools. That is the point ("make it a little bit
// harder"), and it is why nothing here is enforced server-side.
//
// Only wired on the SOLVE path. The /practise review screens and every preview pass
// `isPreview`, where reading back your own graded answer must stay copyable.

// Module-level, so the answer box and the question text share ONE cooldown: a Ctrl+C on
// the stem followed by a Ctrl+V in the box is one attempt, and two toasts for it read as
// two unrelated errors. Also collapses the repeat-fire of a held Ctrl+V.
const TOAST_COOLDOWN_MS = 2000;
let lastToastAt = 0;

/** Show the "copy/paste is off here" toast, at most once per cooldown. */
export const notifyCopyPasteBlocked = (): void => {
  const now = Date.now();
  if (now - lastToastAt < TOAST_COOLDOWN_MS) return;
  lastToastAt = now;
  NotificationManager.info(i18n.t("copy_paste_disabled_open_ended"));
};

/**
 * Cancel a clipboard/drag event and tell the learner why.
 *
 * `copy` and `cut` fire on the element holding the selection and BUBBLE, so one handler
 * on the card root covers Ctrl+C, the right-click menu and the browser's own Edit menu.
 * Deliberately not done with `user-select: none`, which would also stop a learner
 * selecting a line of the question just to keep their place while reading it.
 */
export const blockCopyPasteEvent = (event: any): void => {
  event.preventDefault();
  notifyCopyPasteBlocked();
};

// Test-only: the cooldown is module state, so a test asserting the toast fires must be
// able to clear it between cases.
export const resetCopyPasteToastCooldownForTest = (): void => {
  lastToastAt = 0;
};
